  <!--
    Usage :
    <StaggeredSearch :authenticated="authenticated===true"/>
  -->
  
  <template>
    <div class="input-group m-2 w-auto">
      <div style="border-radius: 16px;border:1px solid #f7f7f7;min-height:36px;background-color:#f7f7f7;margin-left:24px;margin-right:24px;padding-left:16px;width:100%;">
        <font-awesome-icon style="color:#005C8A;" icon="search" size="sm"></font-awesome-icon>
        <input id="search" v-model="unrefinedFilter" type="text" data-test="search" style="padding-left:14px;background-color:#f7f7f7;color:#005C8A;border:0px;margin-top:4px;width:90%;" placeholder="Search for a building or room..." autocomplete="off"/>
      </div>
    </div>
    <Spinner ref="spinner"/>
    <div class="list-group m-2">
      <router-link v-for="result in results"
        :result="result"
        :key="result[0]"
        :to="getUrl(result)"
        class="buildingItem"
        data-test="result">
        {{ result[1] }}
      </router-link>
    </div>
  </template>
  
  <script setup>
  import axios from 'axios'
  import { onMounted, ref, watch } from 'vue'
  import Spinner from '@/components/Spinner.vue'
  
  const aliases = ref([]) // alternate names for buildings
  const filter = ref('') // what is inputted by the user
  const unrefinedFilter = ref('')
  const query = ref('') // appended to /map/locations?q= later
  const items = ref([]) // list from server is stored here
  const buildings = ref([]) // reduced list of buildings stored here to reduce api calls
  const results = ref([]) // filtered list displayed to user
  const stopWords = ref([]) // words to ignore in filter

  const spinner = ref(null)
  
  const props = defineProps({
    authenticated: Boolean
  })

  onMounted(() => { 
    spinner.value.spin()
    axios.get('/map/aliases', { withCredentials: false })
      .then(response => {       
          aliases.value = response.data
          axios.get('/map/buildings', { withCredentials: false })
            .then(response => {
              buildings.value = response.data.map(formatBuilding)
              spinner.value.stop()

              runQuery() // gets inital list
              
              if (props.authenticated) {
                axios.get('/timetable/constants/FINDABUILDINGSTOPWORDS') // gets stopwords from UOFGLIFE_APP_CONSTANT table in timetable database
                .then(response => {
                  stopWords.value = JSON.parse(response.data)
                  applyFilter() // displays list to user
                })
              }


            })
    })
  })
  
  //removes redundant words from filter
  watch(unrefinedFilter, (value) => {
  filter.value = removeStopWords(value)
  })

  watch(filter, async (value) => { // Filters the list data when the filter changes.
  if (query.value !== value.substring(0, 3)) {
    // First 3 chars has changed to re-run server query.
    query.value = value.substring(0, 3)
    await runQuery()
  }
  applyFilter()
  })
  
  function removeStopWords(input) { // removes stop words from input
  let inputList = input.split(" ")
  let outputList = []
  
  for (let inputIndex in inputList) {
    let found = false
    for (let stopIndex in stopWords.value) {
      if (inputList[inputIndex].toLowerCase() === stopWords.value[stopIndex]) { // if word from input is a stop word
        found = true
        break // stops search if it is any of the stop words
      }
    }
  
    if (!found) { // if word was not found, append it to the output
      outputList.push(inputList[inputIndex])
    }
  }
  return outputList.join(" ")
  }  
  
// Runs the query on the server based on the first 3 chars of the filter and waits for the response.
async function runQuery () {
  if (query.value.length === 3) { // if 3 chars inputted then send query to server
    spinner.value.spin()
    const safeQuery = encodeURIComponent(query.value)
    await axios.get('/map/locations?q=' + safeQuery, { withCredentials: false })
      .then(response => {
        items.value = response.data
      })
      .finally(() => {
        spinner.value.stop()
      })
  } else if (query.value.length < 3) { // if user hasn't inputted 3 chars then display reduced list
    items.value = buildings.value
  }
  }

  function applyFilter() { // does local filtering on items
    results.value = []
    items.value.forEach((item) => {
    if (item[1].toLowerCase().indexOf(filter.value.toLowerCase()) >= 0) { // if filter string insdie of item[1]
      results.value.push(item)
    } else {
      for (var i = 0; i < aliases.value.length; i++) {
        if (item[1].indexOf(aliases.value[i].buildingName) >= 0) {
          if (aliases.value[i].aliasText.toLowerCase().indexOf(filter.value.toLowerCase()) >= 0) {
            results.value.push(item)
            break
          }
        }
      }
    }
  })
  }
  
  // takes the type of object returned in "/maps/buildings" and formats it the same way objects returned from "/maps/locations?q=" are formatted
  function formatBuilding(buildingObject) {
  return [buildingObject.buildingId, buildingObject.buildingName]
  }
  
  function getUrl (location) {
    let url = 'map?buildingId='
    url += location[0].substr(0, 3)
    url += '&toId='
    url += location[0]
    url += '&toType='
    if (location[0].length > 3) {
      url += 'R'
    } else {
      url += 'B'
    }
    return url
  }
  </script>
  
  <style>
  .buildingItem:first-of-type {
  border-top: 1px solid #e4e4e4;
  }
  
  .buildingItem {
  background-color:#ffffff;
  color:#003865;
  font-weight:700;
  border-left:0px;
  border-bottom:1px solid #e4e4e4;
  border-right:0px;
  line-height:48px;
  margin-left:24px;
  margin-right:24px;
  height:48px;
  font-family:PTSans;
  }
  </style>